import { Button } from "components/DesignSystem/Button/Button";
import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import { PAID } from "constants/billing";
import { TICKET_PAYMENT } from "constants/ticket";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useLazyGetTicketByIdQuery } from "store/apis/chatTicket";
import { useGetTaskFromChannelIdQuery } from "store/apis/task";
import { actionItemsApi } from "store/apis/actionItems";
import { useChannelStateContext } from "stream-chat-react";
import { BillingInvoice } from "types/Models/billing";
import { CustomData } from "types/Models/reviewDocumentAndBalancingPayment";
import { BackendError } from "types/utils/error";

export const PricedTicketCTA = ({
  customData,
  groupId,
  messageId,
}: {
  customData: CustomData;
  groupId: string;
  messageId: string;
}) => {
  const history = useHistory();
  const paymentModal = useModal();
  const { isCustomer, isForeignCA } = useRoleBasedView();
  const { alertToast } = useToast();
  const dispatch = useDispatch();
  const { channel } = useChannelStateContext();
  const [ticketInvoices, setTicketInvoices] = useState<BillingInvoice[]>([]);
  const [getTicketDetails, { isFetching: isGettingInvoices }] =
    useLazyGetTicketByIdQuery();

  const { data: channelTask } = useGetTaskFromChannelIdQuery(
    { channelId: channel?.id! },
    { skip: !channel?.id }
  );
  const entityId = channelTask?.entity?.uuid;

  const { cta_data, card_status } = customData;
  const ticketId =
    cta_data?.[0]?.metadata?.ticket_id || cta_data?.[1]?.metadata?.ticket_id;
  const isPaymentPending = card_status === TICKET_PAYMENT;

  const openPaymentModal = async () => {
    if (!groupId || !ticketId) return;

    try {
      const data = await getTicketDetails({
        groupId,
        ticketId,
      }).unwrap();

      const invoices =
        data?.ticket_payments
          ?.map((paymentDetail) => paymentDetail.ticket_invoice)
          .filter((invoice) => invoice != null && invoice.status !== PAID) ||
        [];

      setTicketInvoices(invoices as BillingInvoice[]);

      if (invoices.length > 0) {
        paymentModal.open();
      } else {
        alertToast({ message: "No due payments." });
      }
    } catch (e) {
      alertToast({
        message: (e as BackendError)?.data?.error?.message,
      });
    }
  };

  const onInvoicePaid = () => {
    dispatch(
      actionItemsApi.util.invalidateTags([{ type: "ALL_ACTION_ITEMS" }])
    );
    paymentModal.close();
  };

  const redirectToTicket = () => {
    if (isCustomer) {
      history.push(`/settings/tickets?ticketUuid=${ticketId}`);
    } else {
      history.push(`/admin/tickets?ticketUuid=${ticketId}`);
    }
  };

  if (isPaymentPending) {
    return (
      <>
        <Button
          size="small"
          onClick={openPaymentModal}
          isLoading={isGettingInvoices}
          disabled={isGettingInvoices}
        >
          Pay
        </Button>
        {paymentModal.isOpen && entityId && ticketInvoices?.length > 0 && (
          <CheckoutModal
            entityId={entityId}
            type="invoices"
            open={paymentModal.isOpen}
            onClose={paymentModal.close}
            onInvoicePaid={onInvoicePaid}
            invoices={ticketInvoices}
            messageId={messageId}
            payerUserType={isForeignCA && "FCA"}
          />
        )}
      </>
    );
  }

  return (
    <Button size="small" onClick={redirectToTicket}>
      View
    </Button>
  );
};
